import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useFormContext } from "react-hook-form";
import { COLOR_RED, COLOR_PRIMARY, COLOR_LIGHT_RED, COLOR_GRAY, COLOR_LIGHT_GRAY } from "../../constants/color";
import { CloseIcon } from "./icons/CloseIcon";

interface Props {
  name: string;
  label?: string;
  required?: boolean;
  value?: File | null;
  onChange?: (file: File | null) => void;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  disabled?: boolean;
  existingImageUrl?: string | null; // Add this prop for existing images
  onImageRemove?: () => void; // Add callback for when image is removed
  onImageChange?: (file: File | null) => void; // Add callback for when image changes
}

export const ImageUpload: React.FC<Props> = ({
  name,
  label,
  required,
  value,
  onChange,
  error,
  helperText,
  fullWidth,
  disabled,
  existingImageUrl, // New prop
  onImageRemove, // New prop
  onImageChange, // New prop
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageLoadError, setImageLoadError] = useState<boolean>(false);
  const [isExplicitlyRemoved, setIsExplicitlyRemoved] = useState<boolean>(false);
  const formContext = useFormContext();
  const { setValue } = formContext ?? {};

  // Helper function to build image URL that works in both dev and production
  const buildImageUrl = (imagePath: string): string => {
    if (!imagePath) return '';

    // Priority 0: if it's already an absolute URL, return it as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Priority 1: Use dedicated media URL environment variable if available
    if (process.env.REACT_APP_MEDIA_URL) {
      const mediaUrl = process.env.REACT_APP_MEDIA_URL.endsWith('/')
        ? process.env.REACT_APP_MEDIA_URL.slice(0, -1)
        : process.env.REACT_APP_MEDIA_URL;
      return `${mediaUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
    }

    // Priority 2: Use API base URL and construct media URL (works in production)
    const apiBaseUrl = process.env.REACT_APP_API_BASE || '';
    if (apiBaseUrl) {
      try {
        const url = new URL(apiBaseUrl);
        const mediaBaseUrl = `${url.protocol}//${url.host}`;
        // Ensure imagePath starts with / for proper URL construction
        const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
        return `${mediaBaseUrl}${normalizedPath}`;
      } catch (error) {
        console.warn('Invalid API base URL, falling back to relative path');
      }
    }

    // Priority 3: Fallback to relative path (works if frontend and media are on same domain)
    return imagePath;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (file && !file.type.startsWith("image/")) {
      setPreview(null);
      if (onChange) onChange(null);
      setValue?.(name, null, { shouldValidate: true });
      onImageChange?.(null);
      return;
    }
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
      setIsExplicitlyRemoved(false); // Reset explicit removal when new file is selected
    } else {
      setPreview(null);
    }
    if (onChange) onChange(file);
    setValue?.(name, file, { shouldValidate: true });
    onImageChange?.(file); // Call the callback to notify parent component
  };

  const handleRemoveImage = () => {
    setPreview(null);
    setIsExplicitlyRemoved(true); // Track that user explicitly removed the image
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (onChange) onChange(null);
    setValue?.(name, null, { shouldValidate: true });
    onImageRemove?.(); // Call the callback to notify parent component
  };

  // Show preview if value is set from outside (edit mode)
  React.useEffect(() => {
    if (value instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(value);
    } else if (!value) {
      setPreview(null);
    }
  }, [value]);

  // Reset explicit removal state when existingImageUrl changes
  React.useEffect(() => {
    setIsExplicitlyRemoved(false);
    setImageLoadError(false);
  }, [existingImageUrl]);

  // Determine what image to show
  const imageToShow = preview || (existingImageUrl && !imageLoadError && !isExplicitlyRemoved ? buildImageUrl(existingImageUrl) : null);
  const isExistingImage = !preview && existingImageUrl && !imageLoadError && !isExplicitlyRemoved;

  return (
    <StyledContainer $fullWidth={fullWidth}>
      {label && (
        <StyledLabel>
          {label} {required && <StyledRequired>*</StyledRequired>}
        </StyledLabel>
      )}
      <StyledInputWrapper>
        <StyledInput
          type="file"
          accept="image/*"
          id={name}
          // name, ref, and onChange are handled manually for file input
          ref={inputRef}
          onChange={handleFileChange}
          disabled={disabled}
        />
        <StyledButton
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
        >
          {imageToShow ? 'Change Image' : 'Choose Image'}
        </StyledButton>
      </StyledInputWrapper>

      {imageToShow && (
        <StyledPreviewContainer>
          <StyledImageWrapper>
            <StyledPreview
              src={imageToShow}
              alt="Preview"
              onError={() => {
                if (isExistingImage) {
                  console.error('Existing image failed to load:', imageToShow);
                  setImageLoadError(true);
                }
              }}
            />
            {imageToShow && (
              <StyledRemoveButton
                type="button"
                onClick={handleRemoveImage}
                disabled={disabled}
              >
                <CloseIcon />
              </StyledRemoveButton>
            )}
          </StyledImageWrapper>
        </StyledPreviewContainer>
      )}

      {existingImageUrl && imageLoadError && !isExplicitlyRemoved && (
        <StyledErrorContainer>
          <StyledErrorText>Failed to load existing image</StyledErrorText>
        </StyledErrorContainer>
      )}

      {helperText && <StyledHelperText>{helperText}</StyledHelperText>}
      {error && <StyledErrorText>{error}</StyledErrorText>}
    </StyledContainer>
  );
};

const StyledContainer = styled.div<{ $fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $fullWidth }) => $fullWidth && "width: 100%;"}
`;

const StyledLabel = styled.label`
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 2px;
`;

const StyledRequired = styled.span`
  color: ${COLOR_RED};
`;

const StyledInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledInput = styled.input`
  display: none;
`;

const StyledButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid ${COLOR_PRIMARY};
  background: #fff;
  color: ${COLOR_PRIMARY};
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover:not(:disabled) {
    background: ${COLOR_PRIMARY};
    color: #fff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StyledRemoveButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: ${COLOR_LIGHT_GRAY};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: background 0.2s;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  &:hover:not(:disabled) {
    background: ${COLOR_GRAY};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const StyledPreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 8px;
  padding: 6px; /* Add padding to accommodate the close button */
`;

const StyledImageWrapper = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  /* Remove overflow: hidden and border-radius from wrapper */
`;

const StyledPreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px; /* Move border-radius to the image */
  border: 1px solid #eee; /* Move border to the image */
`;

const StyledImageLabel = styled.span`
  font-size: 12px;
  color: #888;
`;

const StyledErrorContainer = styled.div`
  margin-top: 8px;
  padding: 8px;
  background-color: ${COLOR_LIGHT_RED};
  border-radius: 6px;
  border: 1px solid ${COLOR_RED};
`;

const StyledHelperText = styled.div`
  font-size: 12px;
  color: #888;
`;

const StyledErrorText = styled.div`
  font-size: 13px;
  color: ${COLOR_RED};
  margin-top: 2px;
`;